/**
 * Analytics consent (design doc §20.2 Privacy: opt-in per room). The host switches it on in the
 * lobby; every player sees the choice in the lobby and again at Arrival. It is off by default:
 * a room shares nothing unless its host opts in, in front of the table.
 */
import { send, useGame } from "../../lib/store.ts";

export const ANALYTICS_ON =
  "This room shares anonymous game data to improve the game: roles, cards, votes and timings " +
  "under pseudonyms (never names), and an optional survey at the end.";
export const ANALYTICS_OFF =
  "This room does not share game data. Nothing is kept for analysis, and the game's log is " +
  "deleted after the room closes.";

export function AnalyticsConsent({ isHost }: { isHost: boolean }) {
  const room = useGame((s) => s.room);
  const on = room?.analytics === true;
  return (
    <section className="panel stack" data-testid="analytics-consent">
      <div className="row">
        <h3 className="grow" style={{ margin: 0 }}>
          Game data
        </h3>
        {isHost ? (
          <label className="switch">
            <input
              type="checkbox"
              role="switch"
              checked={on}
              disabled={room === null}
              aria-label="Share anonymous game data to improve the game"
              data-testid="analytics-toggle"
              onChange={(e) => void send({ type: "setAnalytics", enabled: e.target.checked })}
            />
            <span>{on ? "Shared" : "Not shared"}</span>
          </label>
        ) : (
          <span className={on ? "tag" : "tag plain"} data-testid="analytics-state">
            {on ? "Shared" : "Not shared"}
          </span>
        )}
      </div>
      {isHost ? (
        <p className="faint" style={{ margin: 0 }}>
          Share anonymous game data to improve the game. Ask the table first: everyone sees this
          choice.
        </p>
      ) : null}
      <p className="muted" style={{ margin: 0 }}>
        {on ? ANALYTICS_ON : ANALYTICS_OFF}
      </p>
    </section>
  );
}

/** The one-line reminder at Arrival. */
export function AnalyticsNotice() {
  const room = useGame((s) => s.room);
  if (!room) return null;
  return (
    <p className="faint center" data-testid="analytics-notice">
      {room.analytics
        ? "The host shares anonymous game data for this game (no names)."
        : "Game data is not shared for this game."}
    </p>
  );
}
